// Catálogo CANÓNICO de planes para los DATOS ESTRUCTURADOS (JSON-LD) de la web
// pública. Es un ESPEJO de la fuente de verdad `GET /public/plans` (respaldada
// por Stripe, que es quien cobra), capturada el 2026-10-31:
//
//   code       nombre público   mensual (USD)   anual total (USD)
//   free       Free             0               0
//   starter    Starter          7               50
//   plus       Plus             8               79
//   pro        Coleccionista    12              119
//   unlimited  Ilimitado        20              199
//
// POR QUÉ ESTE MÓDULO EXISTE (P-41 / P0-2 / P1-4):
// El JSON-LD de `SoftwareApplication` declaraba un `highPrice` falso ("12") que
// divergía del precio real máximo (20). Centralizar los precios del schema en un
// solo módulo evita que el `AggregateOffer` del home/funciones y los `Offer` por
// plan de /precios vuelvan a desincronizarse entre sí.
//
// FUENTE DE VERDAD: la API `/public/plans`, NO este archivo. Este archivo la
// ESPEJA para poder generar el schema en build (el prerender) y en runtime sin
// una llamada de red en tiempo de compilación. Si los precios cambian en Stripe:
//   1) actualizar aquí la tabla PLAN_CATALOG,
//   2) replicar los mismos números en `frontend/scripts/prerender.mjs`
//      (el prerender no importa TS del bundle; mantiene su propia copia),
//   3) el test `planCatalog.test.ts` valida los invariantes (5 planes, lowPrice
//      0, highPrice 20) para que una edición a medias no pase inadvertida.
//
// Precios como NÚMERO ENTERO de dólares (USD); el schema los emite como string.

/** Un plan del catálogo público, con sus precios reales en dólares. */
export interface CatalogPlan {
  /** Código del plan tal como lo expone la API (`free`, `starter`, …). */
  code: string;
  /** Nombre público mostrado en /precios. */
  name: string;
  /** Precio mensual en USD (0 = gratis). */
  priceMonthlyUsd: number;
  /** Precio ANUAL TOTAL en USD (12 meses prepagados; 0 = gratis). */
  priceYearlyUsd: number;
}

/** Moneda de todos los planes (coincide con la API). */
export const PLAN_CURRENCY = 'USD';

/** Tabla canónica de planes (espejo de `/public/plans`, ordenada por precio). */
export const PLAN_CATALOG: readonly CatalogPlan[] = [
  { code: 'free', name: 'Free', priceMonthlyUsd: 0, priceYearlyUsd: 0 },
  { code: 'starter', name: 'Starter', priceMonthlyUsd: 7, priceYearlyUsd: 50 },
  { code: 'plus', name: 'Plus', priceMonthlyUsd: 8, priceYearlyUsd: 79 },
  { code: 'pro', name: 'Coleccionista', priceMonthlyUsd: 12, priceYearlyUsd: 119 },
  { code: 'unlimited', name: 'Ilimitado', priceMonthlyUsd: 20, priceYearlyUsd: 199 },
];

/** Precio mensual más bajo del catálogo (incluye el plan gratuito → 0). */
export function lowestMonthlyUsd(): number {
  return Math.min(...PLAN_CATALOG.map((p) => p.priceMonthlyUsd));
}

/** Precio mensual más alto del catálogo (el tope real, hoy 20). */
export function highestMonthlyUsd(): number {
  return Math.max(...PLAN_CATALOG.map((p) => p.priceMonthlyUsd));
}

/**
 * `AggregateOffer` de schema.org derivado del catálogo real: rango de precios
 * mensuales en USD. Reemplaza el `highPrice` falso que tenía el JSON-LD.
 */
export function aggregateOffer(): Record<string, unknown> {
  return {
    '@type': 'AggregateOffer',
    priceCurrency: PLAN_CURRENCY,
    lowPrice: String(lowestMonthlyUsd()),
    highPrice: String(highestMonthlyUsd()),
    offerCount: String(PLAN_CATALOG.length),
  };
}

/**
 * Lista de `Offer` por plan (nombre + precio mensual) para la página de precios.
 * El precio es el MENSUAL (la cifra que ve el visitante por defecto); el anual
 * va en la descripción para no inventar un segundo `Offer` ambiguo por plan.
 */
export function planOffers(): Array<Record<string, unknown>> {
  return PLAN_CATALOG.map((p) => ({
    '@type': 'Offer',
    name: p.name,
    price: String(p.priceMonthlyUsd),
    priceCurrency: PLAN_CURRENCY,
    description:
      p.priceMonthlyUsd === 0
        ? 'Plan gratuito'
        : `US$${p.priceMonthlyUsd}/mes · US$${p.priceYearlyUsd}/año`,
  }));
}
